import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Paperclip, Bot, User, Sparkles, Trash2, Plus, ChevronLeft } from 'lucide-react';
import { aiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import clsx from 'clsx';

const PROMPT_LIBRARY = [
  'Summarise our CSRD obligations for this year',
  'Which assets need maintenance this month?',
  'Generate a board update for our energy portfolio',
  'What is our carbon position vs target?',
  'Draft an offtaker update letter',
  'Analyse our PPA risk exposure',
  'What are the key regulatory deadlines coming up?',
  'Summarise the financial performance this quarter',
];

const moduleFromPath = (path) => path.split('/')[1] || 'dashboard';

export default function AIChat() {
  const [open, setOpen]               = useState(false);
  const [view, setView]               = useState('chat'); // 'chat' | 'history'
  const [messages, setMessages]       = useState([]);
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConvId]   = useState(null);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [file, setFile]               = useState(null);
  const scrollRef = useRef(null);
  const fileRef   = useRef(null);
  const location  = useLocation();
  const { user }  = useAuth();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const loadConversations = useCallback(async () => {
    try { const { data } = await aiAPI.conversations(); setConversations(data.data || []); }
    catch {}
  }, []);

  const loadConversation = async (id) => {
    try {
      const { data } = await aiAPI.conversation(id);
      setMessages((data.data || []).map(m => ({ role: m.role, content: m.content })));
      setConvId(id);
      setView('chat');
    } catch {}
  };

  const newChat = () => { setMessages([]); setConvId(null); setView('chat'); };

  const deleteConv = async (id, e) => {
    e.stopPropagation();
    try { await aiAPI.deleteConv(id); loadConversations(); if (conversationId === id) newChat(); }
    catch {}
  };

  useEffect(() => { if (open) loadConversations(); }, [open, loadConversations]);

  const send = async (text) => {
    const content = text ?? input;
    if (!content.trim() && !file) return;

    const userMsg = { role: 'user', content, file: file?.name };
    setMessages(m => [...m, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('message', content);
      formData.append('context_module', moduleFromPath(location.pathname));
      if (conversationId) formData.append('conversation_id', conversationId);
      if (file) formData.append('file', file);

      const { data } = await aiAPI.chat(formData);
      setMessages(m => [...m, { role: 'assistant', content: data.data.reply }]);
      if (!conversationId) { setConvId(data.data.conversation_id); loadConversations(); }
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: 'Sorry, I ran into an error. Please try again.', error: true }]);
    } finally { setLoading(false); setFile(null); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-primary-500 text-white shadow-lg hover:bg-primary-600 transition-all hover:scale-105 flex items-center justify-center z-40"
        title="AI Assistant">
        <Sparkles size={22}/>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[620px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-sidebar px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 text-white">
          {view === 'history' && (
            <button onClick={() => setView('chat')} className="text-white/70 hover:text-white mr-1"><ChevronLeft size={16}/></button>
          )}
          <Bot size={16}/>
          <span className="font-medium text-sm">{view === 'history' ? 'Conversation History' : 'AI Assistant'}</span>
          {view === 'chat' && <span className="text-xs text-white/40 capitalize">· {moduleFromPath(location.pathname).replace(/-/g,' ')}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setView('history'); loadConversations(); }} className="text-white/60 hover:text-white" title="History">
            <MessageCircle size={15}/>
          </button>
          <button onClick={newChat} className="text-white/60 hover:text-white" title="New chat">
            <Plus size={15}/>
          </button>
          <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white"><X size={16}/></button>
        </div>
      </div>

      {/* History view */}
      {view === 'history' && (
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <MessageCircle size={28} className="mb-2 text-gray-300"/>
              <p className="text-sm">No conversations yet</p>
            </div>
          ) : conversations.map(c => (
            <div key={c.id} onClick={() => loadConversation(c.id)}
              className="flex items-center justify-between px-4 py-3 hover:bg-hover cursor-pointer border-b border-gray-50 group transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{c.title || 'Untitled conversation'}</p>
                <p className="text-xs text-gray-400 capitalize">{c.context_module || 'general'} · {new Date(c.created_at).toLocaleDateString('en-GB')}</p>
              </div>
              <button onClick={(e) => deleteConv(c.id, e)}
                className="shrink-0 ml-2 p-1 text-gray-300 hover:text-danger opacity-0 group-hover:opacity-100 transition-all">
                <Trash2 size={13}/>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Chat view */}
      {view === 'chat' && (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div>
                <p className="text-xs text-gray-400 mb-2 font-medium">Suggested prompts:</p>
                <div className="space-y-1.5">
                  {PROMPT_LIBRARY.map(p => (
                    <button key={p} onClick={() => send(p)}
                      className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-hover text-primary-700 hover:bg-primary-100 transition-colors leading-relaxed">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={clsx('flex gap-2', m.role === 'user' && 'justify-end')}>
                {m.role === 'assistant' && (
                  <div className="h-7 w-7 rounded-full bg-primary-500 text-white flex items-center justify-center shrink-0">
                    <Bot size={13}/>
                  </div>
                )}
                <div className={clsx(
                  'rounded-xl px-3 py-2 text-sm max-w-[78%] whitespace-pre-wrap leading-relaxed',
                  m.role === 'user' ? 'bg-primary-500 text-white' : m.error ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-800'
                )}>
                  {m.file && <div className="text-xs opacity-70 mb-1 flex items-center gap-1"><Paperclip size={10}/>{m.file}</div>}
                  {m.content}
                </div>
                {m.role === 'user' && (
                  <div className="h-7 w-7 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center shrink-0 text-xs font-bold">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-2">
                <div className="h-7 w-7 rounded-full bg-primary-500 text-white flex items-center justify-center shrink-0"><Bot size={13}/></div>
                <div className="rounded-xl px-3 py-2.5 bg-gray-100 flex gap-1 items-center">
                  <span className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"/>
                  <span className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"/>
                  <span className="h-1.5 w-1.5 bg-gray-400 rounded-full animate-bounce"/>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-100 p-3 shrink-0">
            {file && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2 bg-gray-50 px-2 py-1 rounded-lg w-fit">
                <Paperclip size={10}/>{file.name}
                <button onClick={() => setFile(null)} className="text-gray-400 hover:text-gray-600 ml-1"><X size={10}/></button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <button onClick={() => fileRef.current?.click()} className="text-gray-400 hover:text-gray-600 shrink-0"><Paperclip size={17}/></button>
              <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.csv" className="hidden" onChange={e => setFile(e.target.files[0])}/>
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
                placeholder="Ask anything…"
                className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              />
              <button onClick={() => send()} disabled={loading}
                className="h-8 w-8 rounded-full bg-primary-500 text-white flex items-center justify-center hover:bg-primary-600 transition-colors disabled:opacity-50 shrink-0">
                <Send size={13}/>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
